import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { ExportState, IPrintoutFile } from "../models/boms/bom.model";
import { ProjectStoreModel } from "../models/project-store/project-store.model";
import model from '../models/project-store/_model.json';
import { ProjectService } from "./project.service";
import { IProjectInfo, ProjectInfo } from "../interfaces/IProjectInfo";
import { IComment } from "../interfaces/IElement";
import { PrintChannel } from "src/app/main/svg-component/svg.component";
import { Subject } from "rxjs";
import { ProjectData } from "../project-data";

export interface ICrmObserver {
	modelLoaded(designData: ProjectStoreModel, projectInfo: IProjectInfo, getImages: boolean): void;
	priceChanged(priceValue: number): void;
}

@Injectable({
	providedIn: 'root'
})
export class CrmService {

	private lastDesign: ProjectStoreModel = null;
	private lastDesingDate: Date = null;
	public saveRequest = new Subject<void>();

	//#region observers
	private observers: ICrmObserver[] = []

	public attach(observer: ICrmObserver): void {
		if (this.observers.includes(observer)) {
			return;
		}
		this.observers.push(observer);
	}

	public detach(observer: ICrmObserver): void {
		const observerIndex = this.observers.indexOf(observer);
		if (observerIndex === -1) {
			return;
		}
		this.observers.splice(observerIndex, 1);
	}
	//#endregion

	private crmPresent: boolean = false;
	private crmOrigin: string = "*";
	private affinityId: string = null;

	public get isCrmPresent() {
		if (environment.localhost && this.affinityId) {
			return true;
		} else {
			return this.crmPresent;
		}
	}
	
	constructor(private projectService: ProjectService) {
	}

	public init(affinityId: string): void {
		if (this.affinityId)
			return;
		
		this.affinityId = affinityId;

		window.addEventListener("message", (event) => {
			if (event.data?.affinityId !== this.affinityId) {
				return;
			}

			const msg = event.data?.msg;
			if (!msg) {
				return;
			}

			console.info(event.data);
			switch(msg) {
				case "COMMENTS":
					this.projectService.profileService.setCRMComments(event.data.comments);
					this.postMsg({ msg: "COMMENTS-ACK", affinityId: this.affinityId });
					break;
				case "INIT":
				case "GET_IMAGES":
					this.crmOrigin = event.origin;
					this.crmPresent = true;

					//load the designer data model
					this.observers.forEach(observer => {
						observer.modelLoaded(event.data.designData, event.data.projectInfo, msg == "GET_IMAGES");
						observer.priceChanged(event.data.priceValue);
					});
					break;

				case "PRICE":
					//update the price
					this.observers.forEach(observer => {
						observer.priceChanged(event.data.priceValue);
					});
					break;

				case "SAVE-REQ":
					this.saveRequest.next();
					setTimeout(() => {	
						this.projectService.printProjectSubj.next({ channel: PrintChannel.CRM });
					}, 200);
					break;

				case "ERP-EXPORT":
					this.projectService.erpExportMode = true;
					break;

			}
		});

		window.addEventListener("beforeunload", (event) => {
			this.postMsg({ msg: "CLOSE_TAB", affinityId: this.affinityId });
		});

		this.postMsg({ msg: "READY", affinityId: this.affinityId });

		this.testRestore();

	}

	public loadProject(data: ProjectData): void {
		this.projectService.profileService.comments = [];
		this.projectService.profileService.setComments(data.designerData.comments);

		this.observers.forEach(observer => {
			observer.modelLoaded(data.designerData, data.projectInfo, false);
		});
	}

	public commentChanged(comment: IComment): void {
		if (this.projectService.readOnly) {
			return;
		}
		var cmt = this.projectService.profileService.comments.find(c => c.configId == comment.configId);
		if (cmt) {
			cmt.comment = comment.comment;
		} else {
			this.projectService.profileService.comments.push(comment);
		}
		if (this.isCrmPresent) {
			this.postMsg({ msg: "COMMENTS", affinityId: this.affinityId, comments: [comment] });
		}
	}

	public designChanged(designData: ProjectStoreModel): void {
		if (this.projectService.readOnly) {
			return;
		}
		if (this.isCrmPresent && designData) {
			this.lastDesign = designData;
			this.lastDesingDate = new Date();
			setTimeout(() => {
				this.checkDelay();
			}
			, 1000);
		}
	}

	public exportSave(exportData: IPrintoutFile[]): void {
		if (this.isCrmPresent) {
			this.postMsg({ msg: "SAVE-RES", affinityId: this.affinityId, exportData: exportData });
		}
	}

	public sendCloseRequest(exportState: ExportState = ExportState.None) {
		if (this.isCrmPresent) {
			this.postMsg({ msg: "CLOSE_APP", affinityId: this.affinityId, exportState });
		}
	}
	public sendInitialized() {
		this.postMsg({ msg: "INITIALIZED", affinityId: this.affinityId });
	}

	private checkDelay() {
		if (!this.lastDesingDate) {
			return;
		}
		
		const now = new Date();
		const diff = now.getTime() - this.lastDesingDate.getTime();
		if (diff > 1000) {
			this.postMsg({ msg: "DATA", affinityId: this.affinityId, designData: this.lastDesign });
			this.lastDesingDate = null;
			this.lastDesign = null;
		} else {
			setTimeout(() => {
				this.checkDelay();
			}, 100);
		}
	}

	private postMsg(msg: any) {
		console.info(msg);
		if (window.opener) {
			window.opener.postMessage(msg, this.crmOrigin);
		}
	}

	private testRestore() {
		if (environment.localhost) {
			window.setTimeout(() => {
				this.observers.forEach(observer => {
					var m = this.affinityId == "0" ? null : model;
					var i: IProjectInfo = this.affinityId == "0" ? null :
						new ProjectInfo({
							customerName: "Abdul Majeed Alani Alshmeet",
							customerAddresLine1: "Wienkensfeld, 17",
							customerAddresLine2: "44581, Castrop Rauxel, NRW",
							orderCode: "AT-23030240",
						});
					observer.modelLoaded(m, i, false);
				});
			}, 100);

			window.setTimeout(() => {
				this.observers.forEach(observer => {
					observer.priceChanged(123);
				});
			}, 50);
		}

	}

}
